// TraderProfilePage — Public trader profile (from leaderboard click)
// Layout: hero w/ avatar + tier, P&L stats, follow button, recent trades, public bots

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UserAvatar, TierPill, TierChip } from '../../components/Brand';

interface PublicTrade {
  id: string;
  pair: string;
  direction: 'long' | 'short';
  pnl: number;
  pnlPct: number;
  date: string;
  duration: string;
}

const TRADER = {
  id: 't1',
  name: 'Marcus Chen',
  handle: '@marcus',
  avatar: 'user-1',
  bio: '8+ years trading futures. Specialized in momentum breakouts on indices and metals. Pass rate: 94% across 142 challenges.',
  tier: 'Professional' as const,
  followers: 8420,
  following: 124,
  totalTrades: 1248,
  winRate: 0.78,
  pnl30d: 24850,
  pnlPct30d: 248.5,
  sharpe: 2.41,
  badges: ['verified', 'pro', 'top-10'],
  joinedYear: 2022,
  country: 'US',
};

const TRADES: PublicTrade[] = [
  { id: 'tr1', pair: 'NQ', direction: 'long',  pnl: 420, pnlPct: 0.84, date: '2h ago',   duration: '1h 14m' },
  { id: 'tr2', pair: 'ES', direction: 'long',  pnl: 380, pnlPct: 0.72, date: '5h ago',   duration: '2h 30m' },
  { id: 'tr3', pair: 'GC', direction: 'short', pnl: 280, pnlPct: 0.55, date: '1 day ago', duration: '4h 12m' },
  { id: 'tr4', pair: 'NQ', direction: 'short', pnl: -185, pnlPct: -0.32, date: '2 days ago', duration: '45m' },
  { id: 'tr5', pair: 'ES', direction: 'long',  pnl: 340, pnlPct: 0.64, date: '2 days ago', duration: '1h 45m' },
  { id: 'tr6', pair: 'CL', direction: 'long',  pnl: 220, pnlPct: 0.42, date: '3 days ago', duration: '3h 22m' },
];

export function TraderProfilePage() {
  const nav = useNavigate();
  const { id } = useParams();
  const [following, setFollowing] = useState(false);

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">@{TRADER.handle.replace('@', '')}</div>
        <button onClick={() => alert('More options')} className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
        </button>
      </div>

      {/* Hero */}
      <div className="mx-4 mt-3">
        <div className="ios26-hero text-center" style={{ background: 'linear-gradient(135deg, #FF9500 0%, #FF3B30 100%)', color: 'white' }}>
          <div className="flex justify-center">
            <UserAvatar size={80} name={TRADER.name} />
          </div>
          <div className="mt-3 flex items-center justify-center gap-1.5">
            <h1 className="text-[20px] font-bold tracking-tight">{TRADER.name}</h1>
            <span className="text-[14px]">✓</span>
          </div>
          <div className="text-[12px]" style={{ color: 'rgba(255,255,255,0.85)' }}>{TRADER.handle}</div>
          <div className="mt-2 inline-flex items-center gap-1.5">
            <TierChip tier={TRADER.tier} />
            <span className="rounded-full bg-white/20 backdrop-blur-md px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider">Top 10</span>
          </div>
          <p className="mt-3 text-[12px] max-w-xs mx-auto" style={{ color: 'rgba(255,255,255,0.9)' }}>{TRADER.bio}</p>
        </div>
      </div>

      {/* Actions */}
      <div className="mx-4 mt-3 grid grid-cols-2 gap-2">
        <button
          onClick={() => setFollowing(!following)}
          data-testid="follow-trader"
          className={['h-11 rounded-full font-semibold transition',
            following ? 'bg-[#34C759] text-white' : 'btn-spring'
          ].join(' ')}
        >
          {following ? '✓ Following' : '+ Follow'}
        </button>
        <button onClick={() => nav('/portal/messages/' + TRADER.id)} data-testid="message-trader" className="h-11 rounded-full bg-white border border-tf-border text-[14px] font-semibold text-tf-text-1 active:opacity-40">Message</button>
      </div>

      {/* Stats */}
      <div className="mx-4 mt-3 grid grid-cols-3 gap-2">
        <StatTile label="Followers" value={TRADER.followers.toLocaleString()} />
        <StatTile label="Win rate" value={`${(TRADER.winRate * 100).toFixed(0)}%`} color="#34C759" />
        <StatTile label="Trades" value={TRADER.totalTrades.toLocaleString()} />
      </div>

      {/* Performance hero */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">30-day performance</h2>
      <div className="mx-4 ios26-card p-4">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Net P&L</div>
            <div className="mt-1 dynamic-title text-tf-profit tabular-nums">+${TRADER.pnl30d.toLocaleString()}</div>
            <div className="text-[12px] text-tf-profit">+{TRADER.pnlPct30d.toFixed(1)}%</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Sharpe</div>
            <div className="mt-1 text-[18px] font-bold text-tf-text-1 tabular-nums">{TRADER.sharpe.toFixed(2)}</div>
            <div className="text-[10px] text-tf-text-3">Risk-adj return</div>
          </div>
        </div>
        <svg viewBox="0 0 360 80" preserveAspectRatio="none" className="mt-2 h-20 w-full">
          <defs>
            <linearGradient id="tprof-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#34C759" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#34C759" stopOpacity="0" />
            </linearGradient>
          </defs>
          {(() => {
            const n = 30;
            const out: number[] = [];
            let v = 100;
            for (let i = 0; i < n; i++) { v += Math.sin(i * 0.2) * 8 + 5; out.push(v); }
            const min = Math.min(...out), max = Math.max(...out) - min || 1;
            const path = out.map((p, i) => { const x = (i / (n - 1)) * 360; const y = 80 - ((p - min) / max) * 65 - 8; return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`; }).join(' ');
            return (
              <>
                <path d={path + ' L360 80 L0 80 Z'} fill="url(#tprof-fill)" />
                <path d={path} fill="none" stroke="#34C759" strokeWidth="2" strokeLinecap="round" />
              </>
            );
          })()}
        </svg>
      </div>

      {/* Public bots */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Bots by {TRADER.name.split(' ')[0]}</h2>
      <div className="mx-4 space-y-2">
        {[
          { name: 'Gold Digger', pnl: '+184%', subs: '8.4K', emoji: '🥇', color: '#FF9500' },
          { name: 'Power Hour', pnl: '+72%',  subs: '1.2K', emoji: '💥', color: '#5856D6' },
        ].map((b) => (
          <button key={b.name} onClick={() => nav('/portal/strategy/' + b.name)} className="flex w-full items-center gap-3 rounded-[14px] border border-tf-border bg-white p-3 active:scale-[0.99] transition text-left" data-testid={`public-bot-${b.name}`}>
            <div className="flex h-10 w-10 items-center justify-center rounded-[10px] text-[20px]" style={{ backgroundColor: b.color + '18' }}>{b.emoji}</div>
            <div className="flex-1">
              <div className="text-[14px] font-semibold text-tf-text-1">{b.name}</div>
              <div className="text-[11px] text-tf-text-3">{b.subs} subscribers</div>
            </div>
            <div className="text-[14px] font-bold tabular-nums text-tf-profit">{b.pnl}</div>
          </button>
        ))}
      </div>

      {/* Recent public trades */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Recent trades</h2>
      <div className="mx-4 ios26-list bg-white">
        {TRADES.map((t) => (
          <button
            key={t.id}
            onClick={() => nav('/portal/trade/' + t.id)}
            data-testid={`public-trade-${t.id}`}
            className="flex w-full items-center gap-3 border-b border-tf-border last:border-b-0 px-4 py-3 active:bg-tf-surface-1 transition"
          >
            <div className={['flex h-9 w-9 items-center justify-center rounded-[10px] text-[10px] font-bold',
              t.direction === 'long' ? 'bg-[#34C759]/15 text-tf-profit' : 'bg-[#FF3B30]/15 text-tf-loss'].join(' ')}>
              {t.pair}
            </div>
            <div className="flex-1 text-left">
              <div className="text-[13px] font-semibold text-tf-text-1">{t.pair} · {t.direction.toUpperCase()}</div>
              <div className="text-[11px] text-tf-text-3">{t.date} · {t.duration}</div>
            </div>
            <div className={['text-[14px] font-bold tabular-nums', t.pnl >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
              {t.pnl >= 0 ? '+' : ''}${Math.abs(t.pnl)}
            </div>
          </button>
        ))}
      </div>

      <p className="mt-3 px-4 text-center text-[10px] text-tf-text-3">Public profile · Joined {TRADER.joinedYear} · US</p>
    </div>
  );
}

function StatTile({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="rounded-[12px] border border-tf-border bg-white p-3 text-center">
      <div className="text-[11px] uppercase tracking-wide text-tf-text-3">{label}</div>
      <div className="mt-1 text-[18px] font-bold tabular-nums" style={{ color: color || '#1C1C1E' }}>{value}</div>
    </div>
  );
}
